import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/hooks/useCompany';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Heart, Building2, Mail, ArrowRight } from 'lucide-react';
import { InviteTokenModal } from '@/components/onboarding/InviteTokenModal';

export default function WelcomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, roles, loading } = useAuth();
  const { isCompanyMember, isLoading: companyLoading } = useCompany();
  const [inviteOpen, setInviteOpen] = useState(false);

  if (loading || companyLoading) return null;
  if (!user) return <Navigate to="/auth" replace />;
  // If user already has a role/persona, send them to home
  if (roles.length > 0 || isCompanyMember) return <Navigate to="/app" replace />;

  const options = [
    {
      id: 'family',
      icon: Heart,
      title: t('welcome.family.title'),
      desc: t('welcome.family.desc'),
      cta: t('welcome.family.cta'),
      onClick: () => navigate('/app/patients/new'),
    },
    {
      id: 'company',
      icon: Building2,
      title: t('welcome.company.title'),
      desc: t('welcome.company.desc'),
      cta: t('welcome.company.cta'),
      onClick: () => navigate('/app/company/new'),
    },
    {
      id: 'invite',
      icon: Mail,
      title: t('welcome.invite.title'),
      desc: t('welcome.invite.desc'),
      cta: t('welcome.invite.cta'),
      onClick: () => setInviteOpen(true),
    },
  ];

  return (
    <AppLayout showFAB={false} title={t('welcome.heading')}>
      <div className="p-4 max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-foreground mb-2">{t('welcome.heading')}</h1>
          <p className="text-sm text-muted-foreground">{t('welcome.subtitle')}</p>
        </div>

        <div className="space-y-3">
          {options.map(opt => (
            <Card
              key={opt.id}
              className="cursor-pointer hover:border-primary/60 hover:shadow-md transition-all"
              onClick={opt.onClick}
            >
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
                  <opt.icon className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold">{opt.title}</h3>
                  <p className="text-sm text-muted-foreground">{opt.desc}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <InviteTokenModal open={inviteOpen} onOpenChange={setInviteOpen} />
    </AppLayout>
  );
}
